import React, { useState, useEffect, useContext, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Button } from 'primereact/button';
import Grid from '../../components/components/Grid/Grid';
import { Spinner } from '../../components';
import AccountingAPI from '../services/AccountingAPI';
import CompensationRateGridService from './CompensationRateGridService';
import { CompensationRateReadDto } from './model/CompensationRate';
import LookupService from '../../Services/LookupService';
import { SelectOption } from '../../shared/components/input';
import { useConfirmationModalContext } from '../../shared/components/ModalConfirmationContext';
import { RootState } from '../../shared/store';
import { updateLookupData } from '../../customer/customer-slice/Customer-lookup-slice';
import CarrierManagementAPI from '../../Maintenance/CarrierManagement/Services/CarrierManagementAPI';
import * as constant from '../../shared/util/constant';
import * as label from '../../shared/util/label';
import * as tblConsts from '../../shared/util/tableConstant';
import { AbilityContext } from '../../shared/abilities/AbilityContext';
import './CompensationRateGrid.scss';

interface IGridState {
    rates: any[];
    gridColumns: any[];
    isRowDataAddedOrEdited: boolean;
}

const initState: IGridState = {
    rates: [],
    gridColumns: [],
    isRowDataAddedOrEdited: false,
};

const CompensationRateGrid: React.FC = () => {
    const dispatch = useDispatch();
    const modalContext = useConfirmationModalContext();
    const serviceRef = useRef(new CompensationRateGridService());
    const ability = useContext(AbilityContext);
    const canCreate = ability.can('create', constant.AccountingSubject, constant.AccountingScreenPrivileges.Compensation_Rate);
    const canUpdate = ability.can('update', constant.AccountingSubject, constant.AccountingScreenPrivileges.Compensation_Rate);
    const canDelete = ability.can('delete', constant.AccountingSubject, constant.AccountingScreenPrivileges.Compensation_Rate);

    const reduxAgencyList = useSelector((state: RootState) => state?.LookupState.agencyList);
    const reduxLOBList = useSelector((state: RootState) => state?.LookupState.lineOfBusinessList);
    const reduxStateUSList = useSelector((state: RootState) => state?.LookupState.stateUSList);
    const reduxPolicyTypeList = useSelector((state: RootState) => state?.LookupState.policyTypeList);
    const reduxTransactionTypeList = useSelector((state: RootState) => state?.LookupState.transactionTypeList);

    const [gridState, setGridState] = useState<IGridState>(initState);
    const gridStateRef = useRef(gridState);
    gridStateRef.current = gridState;
    const [navigateToFirstPage, setNavigateToFirstPage] = useState(false);
    const [loading, setLoading] = useState(false);
    const [lookupsLoaded, setLookupsLoaded] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);

    const { rates, gridColumns } = gridState;

    useEffect(() => {
        const fetchLookups = async () => {
            try {
                const service = serviceRef.current;
                const api = new CarrierManagementAPI();

                const [agencyData, lobData, usStateData, policyTypeData, transactionTypeData] = await Promise.all([
                    reduxAgencyList.length > 0 ? Promise.resolve(null) : LookupService.fetchAgencies(),
                    reduxLOBList.length > 0 ? Promise.resolve(null) : LookupService.fetchLineofBusiness(),
                    reduxStateUSList.length > 0 ? Promise.resolve(null) : LookupService.fetchUSStates(),
                    reduxPolicyTypeList.length > 0 ? Promise.resolve(null) : LookupService.fetchPolicyType(),
                    reduxTransactionTypeList.length > 0 ? Promise.resolve(null) : LookupService.fetchTransactionTypes(),
                ]);

                const [carriersData] = await Promise.all([api.getCarriers()]);

                if (agencyData) {
                    const opts = agencyData.map((a: any) => ({ label: a.lookupValue, value: a.id }));
                    dispatch(updateLookupData({ data: opts, type: constant.SlicerLookUpConstant.AGENCY }));
                }
                if (lobData) {
                    const opts = lobData.map((l: any) => ({ label: l.lookupValue, value: l.id }));
                    dispatch(updateLookupData({ data: opts, type: constant.SlicerLookUpConstant.Line_Of_Business }));
                }
                if (usStateData) {
                    const opts = usStateData.map((s: any) => ({ label: s.lookupCode, value: s.lookupCode }));
                    dispatch(updateLookupData({ data: opts, type: constant.SlicerLookUpConstant.US_STATE }));
                }
                if (policyTypeData) {
                    const opts = policyTypeData.map((p: any) => ({ label: p.lookupValue, value: p.id }));
                    dispatch(updateLookupData({ data: opts, type: constant.SlicerLookUpConstant.POLICY_TYPE }));
                }
                if (transactionTypeData) {
                    const opts = transactionTypeData.map((t: any) => ({ label: t.lookupValue, value: t.id }));
                    dispatch(updateLookupData({ data: opts, type: constant.SlicerLookUpConstant.Transaction_Type }));
                }

                const finalAgencyOpts: SelectOption[] = agencyData
                    ? agencyData.map((a: any) => ({ label: a.lookupValue, value: a.id }))
                    : [...reduxAgencyList];
                const finalLOBOpts: SelectOption[] = lobData
                    ? lobData.map((l: any) => ({ label: l.lookupValue, value: l.id }))
                    : [...reduxLOBList];
                const finalStateOpts: SelectOption[] = usStateData
                    ? usStateData.map((s: any) => ({ label: s.lookupCode, value: s.lookupCode }))
                    : [...reduxStateUSList];
                const finalPolicyTypeOpts: SelectOption[] = policyTypeData
                    ? policyTypeData.map((p: any) => ({ label: p.lookupValue, value: p.id }))
                    : [...reduxPolicyTypeList];
                const finalTransactionTypeOpts: SelectOption[] = transactionTypeData
                    ? transactionTypeData.map((t: any) => ({ label: t.lookupValue, value: t.id }))
                    : [...reduxTransactionTypeList];
                const carrierOpts: SelectOption[] = (carriersData || []).map((c: any) => ({ label: c.name, value: c.id }));

                const cols = service.getGridColumns(
                    onEditingCellDataChange,
                    onOriginalRowDataChange,
                    gridCellClickHandler,
                    finalAgencyOpts,
                    carrierOpts,
                    finalStateOpts,
                    finalPolicyTypeOpts,
                    finalLOBOpts,
                    finalTransactionTypeOpts,
                    !canCreate,
                    !canUpdate,
                    !canDelete
                );
                setGridState(prev => ({ ...prev, gridColumns: cols }));
                setLookupsLoaded(true);
            } catch {
                toast.error(constant.ERROR_MESSAGE_LOAD);
            }
        };
        fetchLookups();
    }, []);

    useEffect(() => {
        if (!lookupsLoaded || hasFetched) return;
        fetchData();
    }, [lookupsLoaded, hasFetched]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const service = serviceRef.current;
            const result: CompensationRateReadDto[] = await AccountingAPI.getCompensationRates();
            const rows = result.map((row) => {
                const r = { ...row };
                service.enrichRow(r);
                return r;
            });
            setGridState(prev => ({ ...prev, rates: rows, isRowDataAddedOrEdited: false }));
            setHasFetched(true);
        } catch {
            toast.error(constant.ERROR_MESSAGE_LOAD);
        }
        setLoading(false);
    };

    const onEditingCellDataChange = (editingRow: any, column: any, value: any) => {
        editingRow[column.fieldName] = value;
        setGridState(prev => ({ ...prev, rates: [...prev.rates] }));
    };

    const onOriginalRowDataChange = (originalRow: any, fieldName: string, value: any) => {
        originalRow[fieldName] = value;
        setGridState(prev => ({ ...prev, rates: [...prev.rates] }));
    };

    const duplicateRowHandler = (originalRow: any) => {
        const currentRates = gridStateRef.current.rates;
        const index = currentRates.findIndex((row: any) => row === originalRow);
        if (index === -1) return;
        const newId = constant.DEFAULT_GUID;
        const editingRow = { ...originalRow, id: newId };
        if (editingRow.effDate && typeof editingRow.effDate === 'string') {
            editingRow.effDate = new Date(editingRow.effDate);
        }
        const clonedRow = {
            ...originalRow,
            id: newId,
            enableGridRowEditing: true,
            editingRow,
        };
        const newRates = [...currentRates];
        newRates.forEach(r => { r.isGridInEditMode = true; });
        newRates.splice(index + 1, 0, clonedRow);
        setGridState(prev => ({ ...prev, rates: newRates, isRowDataAddedOrEdited: true }));
    };

    const gridCellClickHandler = (originalRow: any, _column: any, additionalData: any) => {
        switch (additionalData) {
            case 'edit_row': if (canUpdate) editRowClickHandler(originalRow); break;
            case 'save_row': saveRowClickHandler(originalRow); break;
            case 'cancel_row_editing': cancelRowClickHandler(originalRow); break;
            case 'delete_row': if (canDelete) deleteRowClickHandler(originalRow); break;
            case 'duplicate_row': if (canCreate) duplicateRowHandler(originalRow); break;
            default: break;
        }
    };

    const editRowClickHandler = (originalRow: any) => {
        const editingRow = { ...originalRow };
        if (editingRow.effDate && typeof editingRow.effDate === 'string') {
            editingRow.effDate = new Date(editingRow.effDate);
        }
        originalRow.editingRow = editingRow;
        originalRow.enableGridRowEditing = true;

        setGridState(prev => {
            prev.rates.forEach(r => {
                if (r !== originalRow) r.isGridInEditMode = true;
            });
            return { ...prev, rates: [...prev.rates], isRowDataAddedOrEdited: true };
        });
    };

    const saveRowClickHandler = async (originalRow: any) => {
        const editingRow = originalRow.editingRow;
        if (!editingRow) return;

        const validationErrors = serviceRef.current.validateRow(originalRow);
        editingRow.errors = validationErrors;
        if (Object.keys(validationErrors).length > 0) {
            setGridState(prev => ({ ...prev, rates: [...prev.rates] }));
            return;
        }

        try {
            const isNew = !editingRow.id || editingRow.id === constant.DEFAULT_GUID;

            const dto = {
                id: isNew ? constant.DEFAULT_GUID : editingRow.id,
                agencyId: editingRow.agencyId || null,
                carrierId: editingRow.carrierId || null,
                state: editingRow.state || null,
                policyTypeId: editingRow.policyTypeId || null,
                lineOfBusinessId: editingRow.lineOfBusinessId || null,
                transactionTypeId: editingRow.transactionTypeId || null,
                effDate: editingRow.effDate instanceof Date
                    ? editingRow.effDate.toISOString()
                    : (editingRow.effDate || null),
                rate: editingRow.rate != null && editingRow.rate !== '' ? Number(editingRow.rate) : null,
                notes: editingRow.notes || null,
            };

            isNew
                ? await AccountingAPI.createCompensationRate(dto)
                : await AccountingAPI.updateCompensationRate(editingRow.id, dto);

            toast.success(isNew ? constant.CompensationRateConstants.CREATED : constant.CompensationRateConstants.UPDATED);
            setHasFetched(false);
        } catch (ex: any) {
            const msg = ex?.data?.message || ex?.message || constant.CompensationRateConstants.SAVE_ERROR;
            toast.error(msg);
        }
    };

    const cancelRowClickHandler = async (originalRow: any) => {
        const confirmed = await modalContext.showConfirmation(constant.CONFIRM_EXIT);
        if (!confirmed) return;

        setGridState(prev => {
            prev.rates.forEach(r => {
                r.isGridInEditMode = false;
                r.enableGridRowEditing = false;
                r.editingRow = undefined;
            });
            const isNew = !originalRow.id || originalRow.id === constant.DEFAULT_GUID;
            const rows = isNew
                ? prev.rates.filter(r => r !== originalRow)
                : [...prev.rates];
            return { ...prev, rates: rows, isRowDataAddedOrEdited: false };
        });
    };

    const deleteRowClickHandler = async (originalRow: any) => {
        const confirmed = await modalContext.showConfirmation(constant.CompensationRateConstants.DELETE_CONFIRM);
        if (!confirmed) return;

        try {
            await AccountingAPI.deleteCompensationRate(originalRow.id);
            toast.success(constant.CompensationRateConstants.DELETED);
            setHasFetched(false);
        } catch (ex: any) {
            const msg = ex?.data?.message || ex?.message || constant.CompensationRateConstants.DELETE_ERROR;
            toast.error(msg);
        }
    };

    const onAddRowClick = () => {
        setGridState(prev => {
            prev.rates.forEach(r => (r.isGridInEditMode = true));
            const newRow = serviceRef.current.getNewRow();
            return {
                ...prev,
                rates: [newRow, ...prev.rates],
                isRowDataAddedOrEdited: true,
            };
        });
        setNavigateToFirstPage(true);
    };

    return (
        <div className="compensation-rate-grid-container">
            <div className="d-flex justify-content-end mb-2 me-4">
                <Button
                    label={label.CompensationRateLabels.ADD_ROW_LABEL}
                    icon="bi bi-plus-circle"
                    onClick={onAddRowClick}
                    disabled={gridState.isRowDataAddedOrEdited || !canCreate}
                    className="p-button-sm"
                />
            </div>
            {loading && <Spinner loading={true} type="SyncLoader" />}
            {!loading && gridColumns.length > 0 && (
                <div className="compensation-rate-grid-div">
                    <Grid
                        data={rates}
                        showFilters={true}
                        id="compensationRateGrid"
                        retainGridState={true}
                        columns={gridColumns}
                        hiddenColumns={['selection']}
                        sortDefault={[]}
                        paginationDisabled={true}
                        grdTabelClass="table-striped table-sm table-bordered"
                        grdTableContainerClass="compensation-rate-table-container"
                        navigateToFirstPage={navigateToFirstPage}
                        onNavigateToFirstPage={() => setNavigateToFirstPage(false)}
                    />
                </div>
            )}
            {!loading && rates.length === 0 && hasFetched && (
                <div className="text-center text-muted py-3">
                    {constant.CompensationRateConstants.NO_RECORDS_FOUND}
                </div>
            )}
        </div>
    );
};

export default CompensationRateGrid;
